import React, { memo, useMemo } from 'react';

import { Box, Typography } from '@mui/material';
import InlineAlertBanner from '@sharedCR/common/InlineAlertBanner';
import { TableCDS } from '@sharedCR/coreDesignSystem/Table';
import { NoAvailableMessage } from 'app/shared/components/common/NoAvailableMessage';
import PropTypes from 'prop-types';

import { StatusEnum } from 'constants/status';

import { PartiesTestIds } from '../Parties.testIds';

import { PREVIEW_PARTICIPANTS_TABLE_CONFIG } from './DrivePreviewPanel.config';
import {
  MAPPING_DISABLED_MESSAGE,
  NO_PARTICIPANTS_MESSAGE,
  PREVIEW_TITLE,
} from './DrivePreviewPanel.constants';
import { panelStyles } from './DrivePreviewPanel.styles';
import {
  getParticipantsTitle,
  getPreviewIssues,
  getPreviewParticipantRows,
} from './DrivePreviewPanel.utils';

const DrivePreviewPanel = ({ preview = null, families, isLoading }) => {
  const issues = useMemo(() => getPreviewIssues(preview, families), [preview, families]);
  const participantRows = useMemo(() => getPreviewParticipantRows(preview), [preview]);

  const isMappingDisabled = !!preview && !preview.mappingEnabled;
  const isEmpty = !isLoading && !participantRows.length;

  return (
    <Box data-testid={PartiesTestIds.PREVIEW_PANEL} sx={panelStyles}>
      <Typography variant="bold16">{PREVIEW_TITLE}</Typography>

      {isMappingDisabled && (
        <InlineAlertBanner
          dataTestId={PartiesTestIds.PREVIEW_MAPPING_DISABLED}
          message={MAPPING_DISABLED_MESSAGE}
          variant={StatusEnum.INFO}
        />
      )}

      {issues.map(({ id, message }) => (
        <InlineAlertBanner
          dataTestId={PartiesTestIds.PREVIEW_ISSUE}
          key={id}
          message={message}
          variant={StatusEnum.WARNING}
        />
      ))}

      {isEmpty ? (
        <NoAvailableMessage message={NO_PARTICIPANTS_MESSAGE} />
      ) : (
        <Box data-testid={PartiesTestIds.PREVIEW_PARTICIPANTS_TABLE}>
          <TableCDS
            config={PREVIEW_PARTICIPANTS_TABLE_CONFIG}
            data={participantRows}
            isLoading={isLoading}
            title={getParticipantsTitle(participantRows.length)}
          />
        </Box>
      )}
    </Box>
  );
};

DrivePreviewPanel.propTypes = {
  families: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  preview: PropTypes.shape({
    mappingEnabled: PropTypes.bool,
    participants: PropTypes.array,
    unmapped: PropTypes.array,
    overflow: PropTypes.array,
    warnings: PropTypes.arrayOf(PropTypes.string),
  }),
};

export default memo(DrivePreviewPanel);
